// Public proof-of-work gallery — /proof/:token (linked from Status Reply "Photos:" lines)
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, MapPin, Camera, Wrench } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProofGallery() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/public/proof/${token}`).then(async r => {
      if (!r.ok) throw new Error((await r.json()).detail || "Not found");
      setData(await r.json());
    }).catch(e => setErr(e.message));
  }, [token]);

  if (err) return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6"><div className="rugged-card bg-white p-6 text-center max-w-sm" data-testid="proof-error"><div className="font-black uppercase text-lg">Link expired</div><p className="text-sm text-slate-600 mt-1">{err}</p></div></div>;
  if (!data) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm font-bold text-slate-500">Loading photos…</div>;

  const src = (u) => u.startsWith("http") ? u : `${API}${u}`;
  const done = data.status === "completed";
  return (
    <div className="min-h-screen bg-slate-50" data-testid="proof-page">
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10">
        <div className="flex items-center gap-3">
          {data.logo_url ? <img src={src(data.logo_url)} alt="" className="h-10 w-10 object-contain"/> : <Wrench weight="fill" size={28} className="text-[#00C4CC]"/>}
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{data.company || "Field Radar"} · Proof of work</div>
        </div>
        <h1 className="mt-4 font-display font-black text-2xl sm:text-4xl uppercase tracking-tight" data-testid="proof-title">{data.title}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm font-bold text-slate-700">
          <span className="inline-flex items-center gap-1"><MapPin weight="fill" size={14}/>{data.property_address}{data.unit ? ` · Apt ${data.unit}` : ""}</span>
          {data.external_wo && <span className="font-mono text-xs bg-[#0f172a] text-white px-1.5 py-0.5">WO {data.external_wo}</span>}
          {done && <span className="inline-flex items-center gap-1 text-emerald-700" data-testid="proof-completed"><CheckCircle weight="fill" size={14}/> Completed {data.completed_at ? new Date(data.completed_at).toLocaleDateString() : ""}{data.tech ? ` by ${data.tech}` : ""}</span>}
        </div>
        {data.completion_notes && <p className="mt-3 text-sm text-slate-700 border-l-4 border-[#00C4CC] pl-3" data-testid="proof-notes">{data.completion_notes}</p>}

        <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Camera weight="fill" size={12}/> After photos ({data.photos.length})</div>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="proof-photos">
          {data.photos.map((p, i) => (
            <button key={i} onClick={() => setLightbox(src(p.url))} className="aspect-square bg-white border-2 border-slate-900 overflow-hidden" data-testid={`proof-photo-${i}`}>
              <img src={src(p.url)} alt={p.caption || `After photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy"/>
            </button>
          ))}
        </div>
        <p className="mt-6 text-[11px] text-slate-400 font-medium">Read-only link · expires {data.expires_at ? new Date(data.expires_at).toLocaleDateString() : ""}</p>
      </div>
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => setLightbox(null)} data-testid="proof-lightbox">
          <img src={lightbox} alt="" className="max-h-full max-w-full object-contain"/>
        </div>
      )}
    </div>
  );
}
